import React, { useState } from "react";
import PropTypes from "prop-types";
import { PaperclipIcon, AlertCircleIcon } from "../../../../Icons";

const formatBytes = (n) => {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Printable attachment block shown after its parent form in bulk print.
 * Images render inline; PDFs embed in an iframe; other types show a notice.
 */
const ClinicalFormAttachmentPrintBlock = ({ attachment, formName }) => {
  const [imgError, setImgError] = useState(false);
  if (!attachment) return null;

  const title = attachment.displayName || attachment.originalName || "Attachment";

  return (
    <section className="print-attachment mx-auto w-full max-w-4xl rounded-xl border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-8">
      <header className="mb-3 flex items-start gap-2 border-b border-slate-100 pb-2">
        <PaperclipIcon size={14} className="mt-0.5 shrink-0 text-indigo-600" />
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Attachment{formName ? ` · ${formName}` : ""}
          </p>
          <h2 className="truncate text-sm font-semibold text-slate-900">
            {title}
          </h2>
          <p className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
            {attachment.mimeType || "file"}
            {attachment.size != null ? ` · ${formatBytes(attachment.size)}` : ""}
          </p>
        </div>
      </header>

      {attachment.printable && attachment.viewUrl && attachment.kind === "image" && !imgError ? (
        <div className="flex justify-center bg-slate-50 rounded-lg p-2">
          <img
            src={attachment.viewUrl}
            alt={title}
            className="max-h-[1400px] max-w-full object-contain"
            onError={() => setImgError(true)}
          />
        </div>
      ) : null}

      {attachment.printable && attachment.viewUrl && attachment.kind === "pdf" ? (
        <div className="print-pdf-embed overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          <object
            data={`${attachment.viewUrl}#toolbar=0&navpanes=0`}
            type="application/pdf"
            className="h-[1100px] w-full"
            aria-label={title}
          >
            <iframe
              title={title}
              src={attachment.viewUrl}
              className="h-[1100px] w-full border-0"
            />
          </object>
          <p className="no-print px-3 py-2 text-[11px] text-slate-500 border-t border-slate-200">
            If the PDF preview is blank, open the file separately or use Print /
            Save as PDF from your browser.
          </p>
        </div>
      ) : null}

      {(!attachment.printable || !attachment.viewUrl || imgError) &&
      attachment.kind !== "pdf" ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900">
          <AlertCircleIcon size={14} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">{title}</p>
            <p className="mt-0.5 text-amber-800">
              {imgError
                ? "Image could not be loaded for print preview."
                : attachment.skipReason ||
                  "This attachment cannot be embedded in the print preview."}
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
};

ClinicalFormAttachmentPrintBlock.propTypes = {
  attachment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    displayName: PropTypes.string,
    originalName: PropTypes.string,
    mimeType: PropTypes.string,
    size: PropTypes.number,
    kind: PropTypes.string,
    printable: PropTypes.bool,
    viewUrl: PropTypes.string,
    skipReason: PropTypes.string,
  }),
  formName: PropTypes.string,
};

export default ClinicalFormAttachmentPrintBlock;
